import { DateTime } from 'luxon';

export const SLOTS_PER_DAY = 48;
export const TOTAL_SLOTS = 336;

export function utcInstantToBitIdx(d: Date): number {
  const dt = DateTime.fromJSDate(d, { zone: 'utc' });
  const dayMon0 = dt.weekday - 1;
  const halfHour = dt.hour * 2 + Math.floor(dt.minute / 30);
  return dayMon0 * SLOTS_PER_DAY + halfHour;
}

export function bitIdxToUtcInstant(weekStartIso: string, bitIdx: number): Date {
  const wed = DateTime.fromISO(weekStartIso, { zone: 'utc' }).startOf('day');
  const monOfBitString = wed.minus({ days: 2 });
  const dayMon0 = Math.floor(bitIdx / SLOTS_PER_DAY);
  const halfHour = bitIdx % SLOTS_PER_DAY;
  return monOfBitString.plus({ days: dayMon0, minutes: halfHour * 30 }).toJSDate();
}

/** Start of a half-hour cell in `userTz` for the lockout week anchored at `weekStartIso` (Wednesday UTC). */
export function lockoutHalfHourToDateTime(
  weekStartIso: string,
  dayLocalMon0: number,
  halfHourLocal: number,
  userTz: string
): DateTime {
  const wedUtc = DateTime.fromISO(weekStartIso, { zone: 'utc' }).startOf('day');
  const monUtcDate = wedUtc.minus({ days: 2 }).toISODate();
  if (!monUtcDate) {
    throw new Error(`Invalid weekStartIso: ${weekStartIso}`);
  }
  return DateTime.fromISO(monUtcDate, { zone: userTz }).plus({
    days: dayLocalMon0,
    minutes: halfHourLocal * 30,
  });
}

export function localCellToBitIdx(
  weekStartIso: string,
  dayLocalMon0: number,
  halfHourLocal: number,
  userTz: string
): number {
  const local = lockoutHalfHourToDateTime(weekStartIso, dayLocalMon0, halfHourLocal, userTz);
  return utcInstantToBitIdx(local.toUTC().toJSDate());
}

function floorToHalfHourUserTz(dt: DateTime): DateTime {
  const z = dt.set({ second: 0, millisecond: 0 });
  const flooredMin = Math.floor(z.minute / 30) * 30;
  return z.set({ minute: flooredMin });
}

/**
 * Maps a UTC session interval to half-hour availability bits using the same geometry as
 * `localCellToBitIdx` (not `utcInstantToBitIdx`, which uses UTC calendar weekdays only).
 */
export function utcRangeToAvailabilityBitRanges(
  weekStartIso: string,
  startUtc: Date,
  endUtc: Date,
  userTz: string
): [number, number][] {
  // The 336-bit availability string is a weekly pattern indexed by
  // utcInstantToBitIdx (UTC weekday, mod 7 days) — the SAME geometry
  // AvailabilityGrid writes with via localCellToBitIdx. We must NOT bound the
  // session to the [weekMon, weekMon+7) calendar span: the lockout week's own
  // Monday/Tuesday are weekStart+5 / weekStart+6 and fall OUTSIDE that span,
  // which made this return [] for every Mon/Tue raid and caused
  // isCharacterAvailable() to fail-open ("everyone available"). Verified
  // against raid efb622a1 (Tue 2026-05-19 18:00Z): produces {84..91}.
  const startZ = DateTime.fromJSDate(startUtc, { zone: 'utc' }).setZone(userTz);
  const endZ = DateTime.fromJSDate(endUtc, { zone: 'utc' }).setZone(userTz);

  let cur = floorToHalfHourUserTz(startZ);
  if (cur >= endZ) return [];

  const bits = new Set<number>();
  while (cur < endZ) {
    // Map this half-hour instant directly to its canonical pattern bit,
    // exactly as the grid does (localCellToBitIdx -> utcInstantToBitIdx).
    bits.add(utcInstantToBitIdx(cur.toUTC().toJSDate()));
    cur = cur.plus({ minutes: 30 });
  }

  const sorted = Array.from(bits).sort((a, b) => a - b);
  const ranges: [number, number][] = [];
  let i = 0;
  while (i < sorted.length) {
    const lo = sorted[i];
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    ranges.push([lo, Math.min(sorted[j] + 1, TOTAL_SLOTS)]);
    i = j + 1;
  }
  return ranges;
}

export function bitIdxToLocalCell(
  weekStartIso: string,
  bitIdx: number,
  userTz: string
): { dayLocalMon0: number; halfHourLocal: number; date: Date } {
  const utcInstant = bitIdxToUtcInstant(weekStartIso, bitIdx);
  const local = DateTime.fromJSDate(utcInstant, { zone: 'utc' }).setZone(userTz);
  const dayLocalMon0 = local.weekday - 1;
  const halfHourLocal = local.hour * 2 + Math.floor(local.minute / 30);
  return { dayLocalMon0, halfHourLocal, date: utcInstant };
}

export function formatRaidTime(startsAtIso: string, userTz: string) {
  const utc = DateTime.fromISO(startsAtIso, { zone: 'utc' });
  const zoned = utc.setZone(userTz);
  const dt = zoned.isValid ? zoned : utc;
  return {
    time: dt.toFormat('h:mm a'),
    date: dt.toFormat('EEE, MMM d'),
    weekday: dt.toFormat('EEEE'),
    tzAbbr: zoned.isValid ? dt.toFormat('ZZZZ') : 'UTC',
    full: zoned.isValid
      ? dt.toFormat("EEE, MMM d 'at' h:mm a ZZZZ")
      : dt.toFormat("EEE, MMM d 'at' h:mm a 'UTC'"),
  };
}

// ── Weekly reset ───────────────────────────────────────────────────────────
//
// Two different things live here, and conflating them is what made this wrong
// for US realms in the first place:
//
//   the LABEL    `week_start`, a Wednesday-UTC date. The key on every weekly
//                row (availability, manual lockouts, guild release, bot
//                messages). Identical for every region, on purpose.
//   the INSTANT  when the week actually flips. Region-specific: EU Wednesday
//                03:00 UTC, US Tuesday 08:00 Pacific.
//
// A US lockout week runs Tue 15:00Z → next Tue 15:00Z and is labelled with the
// Wednesday inside it. Keeping the label a Wednesday for everyone means no
// migration, no change to the 336-bit grid's Monday anchor (a Wednesday label
// minus 2 days is still Monday), and the existing "must be a Wednesday"
// validators keep guarding the invariant instead of having to be relaxed.
//
// Rules are DB config on the region game_option (see auth/migrate_region_reset.js),
// delivered to the client by GET /games/:gameId/fields as option_metadata.
export interface ResetRule {
  /** ISO weekday of the reset: Mon=1 .. Sun=7. */
  weekday: number;
  /** Wall-clock hour of the reset, read in `tz`. */
  hour: number;
  /** IANA zone the wall clock is read in. 'UTC' for regions anchored to UTC. */
  tz: string;
}

/** EU. Also the fallback for any region with no rule configured. */
export const DEFAULT_RESET_RULE: ResetRule = { weekday: 3, hour: 3, tz: 'UTC' };

/** Reads a rule off a region option's `metadata`, falling back to EU. */
export function resetRuleFrom(metadata: any): ResetRule {
  const r = metadata?.reset;
  if (
    r &&
    Number.isInteger(r.weekday) && r.weekday >= 1 && r.weekday <= 7 &&
    Number.isInteger(r.hour) && r.hour >= 0 && r.hour <= 23 &&
    typeof r.tz === 'string' && r.tz
  ) {
    return { weekday: r.weekday, hour: r.hour, tz: r.tz };
  }
  return DEFAULT_RESET_RULE;
}

/** The Wednesday-UTC label for the lockout week that `instant` starts. */
function labelForResetInstant(instant: DateTime): string {
  const d0 = instant.toUTC().startOf('day');
  // The one Wednesday inside [instant, instant + 7d).
  return d0.plus({ days: (3 - d0.weekday + 7) % 7 }).toISODate() || '';
}

/** The reset instant that started the lockout week containing `d`. */
export function resetInstantBefore(d: Date, rule: ResetRule = DEFAULT_RESET_RULE): DateTime {
  const z = DateTime.fromJSDate(d, { zone: 'utc' }).setZone(rule.tz);
  // Wall clock is set before the day arithmetic so a DST jump moves the
  // instant, not the hour: 08:00 Pacific stays 08:00 Pacific across the change.
  let cand = z.startOf('day').set({ hour: rule.hour, minute: 0, second: 0, millisecond: 0 });
  cand = cand.minus({ days: (cand.weekday - rule.weekday + 7) % 7 });
  if (cand > z) cand = cand.minus({ days: 7 });
  return cand;
}

/** The reset instant that STARTS the week labelled `weekLabelIso`. */
export function resetInstantForWeek(weekLabelIso: string, rule: ResetRule = DEFAULT_RESET_RULE): DateTime {
  const wed = DateTime.fromISO(weekLabelIso, { zone: rule.tz }).startOf('day');
  return wed
    .minus({ days: (wed.weekday - rule.weekday + 7) % 7 })
    .set({ hour: rule.hour, minute: 0, second: 0, millisecond: 0 });
}

/** Exclusive end of the week labelled `weekLabelIso` (= the next reset). */
export function resetWeekEnd(weekLabelIso: string, rule: ResetRule = DEFAULT_RESET_RULE): DateTime {
  return resetInstantForWeek(weekLabelIso, rule).plus({ days: 7 });
}

export function getResetWeekForDate(d: Date, rule: ResetRule = DEFAULT_RESET_RULE): string {
  return labelForResetInstant(resetInstantBefore(d, rule));
}

export function getCurrentResetWeek(rule: ResetRule = DEFAULT_RESET_RULE): string {
  return getResetWeekForDate(new Date(), rule);
}

export function getNextResetWeek(rule: ResetRule = DEFAULT_RESET_RULE): string {
  return DateTime.fromISO(getCurrentResetWeek(rule), { zone: 'utc' }).plus({ days: 7 }).toISODate() || '';
}

/** UTC ISO → values for native date / time / datetime-local inputs in the user's zone. */
export function utcToPickerParts(
  utcIso: string,
  userTz: string
): { date: string; time: string; datetimeLocal: string } {
  const dt = DateTime.fromISO(utcIso, { zone: 'utc' }).setZone(userTz);
  if (!dt.isValid) {
    const utc = DateTime.fromISO(utcIso, { zone: 'utc' });
    const u = utc.isValid ? utc : DateTime.utc();
    return {
      date: u.toFormat('yyyy-MM-dd'),
      time: u.toFormat('HH:mm'),
      datetimeLocal: u.toFormat("yyyy-MM-dd'T'HH:mm"),
    };
  }
  return {
    date: dt.toFormat('yyyy-MM-dd'),
    time: dt.toFormat('HH:mm'),
    datetimeLocal: dt.toFormat("yyyy-MM-dd'T'HH:mm"),
  };
}

/** date input (yyyy-MM-dd) + time (HH:mm) in userTz → UTC ISO string. */
export function pickerPartsToUtc(date: string, time: string, userTz: string): string {
  const dt = DateTime.fromISO(`${date}T${time}`, { zone: userTz });
  if (!dt.isValid) {
    const fallback = DateTime.fromISO(`${date}T${time}`, { zone: 'utc' });
    return (fallback.isValid ? fallback : DateTime.utc()).toISO() || '';
  }
  return dt.toUTC().toISO() || '';
}

function coerceClockMinutesToHHmm(totalMinutes: number): string {
  let t = totalMinutes;
  const max = 24 * 60;
  t = ((t % max) + max) % max;
  const render = t === max ? 0 : t;
  const outH = Math.floor(render / 60)
    .toString()
    .padStart(2, '0');
  const outM = (render % 60).toString().padStart(2, '0');
  return `${outH}:${outM}`;
}

/** Move a wall clock in `userTz` by delta minutes (DST-safe). Used for raid schedule +/- controls. */
export function shiftWallClock(
  dateYmd: string,
  timeHHmm: string,
  deltaMinutes: number,
  userTz: string
): { date: string; time: string } {
  const ymd = String(dateYmd || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd) || !timeHHmm) {
    const [hStr, mStr] = (timeHHmm || '00:00').split(':');
    const base = (Number(hStr) || 0) * 60 + (Number(mStr) || 0) + deltaMinutes;
    return { date: ymd || dateYmd, time: coerceClockMinutesToHHmm(base) };
  }
  const dt = DateTime.fromISO(`${ymd}T${timeHHmm}`, { zone: userTz });
  if (!dt.isValid) {
    const [hStr, mStr] = timeHHmm.split(':');
    const base = (Number(hStr) || 0) * 60 + (Number(mStr) || 0) + deltaMinutes;
    return { date: ymd, time: coerceClockMinutesToHHmm(base) };
  }
  const next = dt.plus({ minutes: deltaMinutes });
  return { date: next.toFormat('yyyy-MM-dd'), time: next.toFormat('HH:mm') };
}

/** datetime-local value in userTz → UTC ISO string. */
export function datetimeLocalToUtc(local: string, userTz: string): string {
  const dt = DateTime.fromISO(local, { zone: userTz });
  if (!dt.isValid) {
    const fallback = DateTime.fromISO(local, { zone: 'utc' });
    return (fallback.isValid ? fallback : DateTime.utc()).toISO() || '';
  }
  return dt.toUTC().toISO() || '';
}

/** Hero / list: one line per session start in the user's zone (matches legacy home copy shape). */
export function formatHomeRaidSessionsLabel(
  sessions: { starts_at: string }[] | undefined,
  anchorDateYmd: string | null | undefined,
  userTz: string
): string {
  const fmtSession = (iso: string) => {
    const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(userTz);
    if (!dt.isValid) return '—';
    return dt.toFormat('EEE d MMM, h:mm a');
  };
  if (sessions && sessions.length > 0) {
    const parts = sessions.map((s) => fmtSession(s.starts_at));
    if (parts.length === 1) return parts[0];
    return `${parts[0]} (+${parts.length - 1})`;
  }
  if (anchorDateYmd) {
    const ymd = String(anchorDateYmd).slice(0, 10);
    const dt = DateTime.fromISO(ymd, { zone: 'utc' }).startOf('day').setZone(userTz);
    if (!dt.isValid) return 'TBD';
    return dt.toFormat('ccc, MMM d, yyyy');
  }
  return 'TBD';
}

/** Millisecond countdown from now to target (wall-neutral; both are UTC instants). */
export function formatCountdownToMs(targetMs: number, nowMs: number): string {
  const diff = targetMs - nowMs;
  if (diff <= 0) return 'Starting Soon';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

/** Schedule strip: local same-day offset + 24h start/end in userTz. */
export function formatScheduleRaidTimes(
  anchorInstantMs: number,
  endInstantMs: number,
  userTz: string,
  nowMs: number
): { weekday: string; startTime: string; endTime: string; dayLabel: string } {
  const start = DateTime.fromMillis(anchorInstantMs, { zone: 'utc' }).setZone(userTz);
  const end = DateTime.fromMillis(endInstantMs, { zone: 'utc' }).setZone(userTz);
  const now = DateTime.fromMillis(nowMs, { zone: userTz });
  if (!start.isValid) {
    return { weekday: '—', startTime: '--:--', endTime: '--:--', dayLabel: '' };
  }
  const startDay = start.startOf('day');
  const todayStart = now.startOf('day');
  const dayDiff = Math.max(0, Math.round(startDay.diff(todayStart, 'days').days));
  return {
    weekday: start.toFormat('ccc'),
    startTime: start.toFormat('HH:mm'),
    endTime: end.isValid ? end.toFormat('HH:mm') : '--:--',
    dayLabel: dayDiff === 0 ? 'Today' : `In ${dayDiff} day${dayDiff === 1 ? '' : 's'}`,
  };
}

export function formatIanaTzShortLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || tz;
  } catch {
    return tz;
  }
}

/** 336-bit week indices → local range label (same geometry as `localCellToBitIdx` / overlap heatmap). */
export function formatBitRangeLocalLabel(
  weekStartIso: string,
  startBit: number,
  endBit: number,
  userTz: string
): string {
  const a = Math.max(0, Math.min(TOTAL_SLOTS - 1, Math.floor(startBit)));
  const b = Math.max(0, Math.min(TOTAL_SLOTS - 1, Math.floor(endBit)));
  if (b < a) return '—';
  const startDt = DateTime.fromJSDate(bitIdxToUtcInstant(weekStartIso, a), { zone: 'utc' }).setZone(userTz);
  const endExclusive = DateTime.fromJSDate(bitIdxToUtcInstant(weekStartIso, b), { zone: 'utc' })
    .plus({ minutes: 30 })
    .setZone(userTz);
  if (!startDt.isValid || !endExclusive.isValid) return '—';
  const lastMoment = endExclusive.minus({ milliseconds: 1 });
  const sameDay = startDt.hasSame(lastMoment, 'day');
  if (sameDay) {
    return `${startDt.toFormat('ccc HH:mm')}–${endExclusive.toFormat('HH:mm')}`;
  }
  return `${startDt.toFormat('ccc HH:mm')}–${endExclusive.toFormat('ccc HH:mm')}`;
}
